import { Axios } from 'axios';
import { ILogReqParams, ILogResDto } from 'components/log/types/dtos';
import { IUserSettingsResDto } from 'components/settings/types/user-settings';
import { ITwoFACheckIsActiveRes, ITwoFAKeyResDto, ITwoFAToggleDto } from 'components/twoFA/types/dtos';
import { TableQueryParams } from 'hooks/table-params';
import apiClient from 'services/api/axios';
import { IApiErrorDtoRes } from 'store/api-error/types/api-error';
import {
  IChangeSelfPasswordBody, IGetProfileResDto,
  IGetUserServicesDto, IRegionsResDto, ITwoFAEnforcementDtoRes, IUserLoginResBody,
} from 'store/user/types/api-bodies.types';
import {
  ApiKeyCreateDto, ApiKeyEditDto, IApiKeyListDto, IGenerateKeyResDto, IKeyPermissionsDto,
} from 'store/user/types/keys';
import { IDashboardResources } from 'store/virtual-machine/types/api-bodies.types';

class ProfileService {
  api: Axios;

  constructor(api: Axios) {
    this.api = api;
  }

  getProfile() {
    return this.api.post<IGetProfileResDto>('user/me');
  }

  getUserRegions() {
    return this.api.get<IRegionsResDto>('/panel/regions');
  }

  setRegion(regionId: number) {
    return this.api.post(`user/setRegion?id=${regionId}`);
  }

  setLanguage(language: string) {
    return this.api.post('panel/setLanguage', undefined, { params: { language } });
  }

  signOut() {
    return this.api.post('v1/auth/logout');
  }

  getErrorsMap() {
    return this.api.get<IApiErrorDtoRes>('panel/getAllErrorCodes');
  }

  changeSelfPassword(body: IChangeSelfPasswordBody, lang?: string) {
    return this.api.post<IUserLoginResBody>('v1/auth/change-password', body, {
      headers: lang ? {
        'X-Lang': lang,
      } : undefined,
    });
  }

  getLog(params: ILogReqParams) {
    return this.api.post<ILogResDto>('report/data', params);
  }

  getToFAKey(login: string) {
    return this.api.get<ITwoFAKeyResDto>(`2fa/qrcode/get/${login}`);
  }

  checkTwoFAPassword(password: string, lang: string) {
    return this.api.post('2fa/checkUserPassFor2Fa', { password }, {
      headers: {
        'X-lang': lang,
      },
    });
  }

  checkIsTwoFAActive() {
    return this.api.get<ITwoFACheckIsActiveRes>('/2fa/getUserTwoFaIsOn');
  }

  toggleTwoFA({ otp, turnOn }: ITwoFAToggleDto) {
    return this.api.post('2fa/turnOnTwoFa', { totpKey: Number(otp), turnOn });
  }

  getSettings(userId: number) {
    return this.api.get<IUserSettingsResDto>(`user-settings/user/${userId}`);
  }

  getAccessKeys(queryParams: TableQueryParams) {
    return this.api.get<IApiKeyListDto>('api/v1/iam/api-keys', {
      baseURL: '/panel-main',
      params: queryParams,
    });
  }

  getKeyPermissions() {
    return this.api.get<IKeyPermissionsDto>('api/v1/iam/permissions', {
      baseURL: '/panel-main',
    });
  }

  generateKey(dto: ApiKeyCreateDto) {
    return this.api.post<IGenerateKeyResDto>('/api/v1/iam/api-keys', dto, {
      baseURL: '/panel-main',
    });
  }

  editKey(dto: ApiKeyEditDto, id: number) {
    return this.api.patch(`/api/v1/iam/api-keys/${id}/name`, dto, {
      baseURL: '/panel-main',
    });
  }

  deleteKey(keyId: number) {
    return this.api.delete(`/api/v1/iam/api-keys?ids=${keyId}`, {
      baseURL: '/panel-main',
    });
  }

  getIsStorageEnabled(subUserId?: number) {
    return this.api.get<boolean>(
      '/services/api/v1/is-storage-enabled',
      {
        baseURL: '/panel-main',
        params: {
          subUserId,
        },
      },
    );
  }

  enableStorageAccess(subUserId?: number) {
    return this.api.patch(
      '/services/api/v1/enable-storage',
      undefined,
      {
        baseURL: '/panel-main',
        params: {
          subUserId,
        },
      },
    );
  }

  getServices() {
    return this.api.get<IGetUserServicesDto>('/services/api/v1/services', {
      baseURL: '/panel-main',
    });
  }

  deactivateRequest() {
    return this.api.post('user/deactivate-request ');
  }

  getDashboardResources(region?: string | number) {
    return this.api.get<IDashboardResources>('/v2/dashboard/resources', {
      params: { region },
    });
  }

  get2FAEnforcement() {
    return this.api.get<ITwoFAEnforcementDtoRes>('2fa/enforcement');
  }

  postpone2FAEnforcement() {
    return this.api.post('2fa/enforcement/postpone');
  }
}

export default new ProfileService(apiClient);
